// client/src/components/CashierPromo.js

import React, { useState, useEffect, useMemo } from 'react';
import { firestore, storage } from '../../firebase';
import { getDoc, doc, onSnapshot } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { ref, listAll, getDownloadURL, getMetadata } from 'firebase/storage';
import '../CSS/Dashboard.css';
import { useNavigate } from 'react-router-dom';
import CashierNavbar from './CashierNavbar';

/* ============================
   Helpers (même logique, UI only)
============================ */
const kindFromContentType = (ct = '') => {
  if (!ct) return null;
  if (ct.startsWith('image/')) return 'image';
  if (ct === 'application/pdf') return 'pdf';
  if (ct.startsWith('video/')) return 'video';
  return 'other';
};
const kindFromUrlExt = (url = '') => {
  const lower = (url || '').toLowerCase();
  if (lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.webp')) return 'image';
  if (lower.endsWith('.pdf')) return 'pdf';
  if (lower.endsWith('.mp4')) return 'video';
  return 'other';
};

// Petit utilitaire cache (5 minutes)
const CACHE_TTL_MS = 5 * 60 * 1000;
const readCache = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !obj.fetchedAt) return null;
    if (Date.now() - obj.fetchedAt > CACHE_TTL_MS) return null;
    return obj;
  } catch { return null; }
};
const writeCache = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify({ ...data, fetchedAt: Date.now() }));
  } catch {}
};

const Thumb = React.memo(function Thumb({ pub }) {
  const kind = useMemo(() => {
    const byMeta = kindFromContentType(pub.contentType);
    return byMeta || kindFromUrlExt(pub.url);
  }, [pub.contentType, pub.url]);

  const mediaStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    pointerEvents: 'none',
  };

  if (kind === 'image') {
    return <img loading="lazy" src={pub.url} alt={pub.name || 'Promotion'} style={mediaStyle} />;
  }
  if (kind === 'video') {
    return <video src={pub.url} muted playsInline preload="metadata" style={mediaStyle} />;
  }
  if (kind === 'pdf') {
    return (
      <iframe
        title={pub.name || 'PDF'}
        src={`${pub.url}#zoom=page-fit&scrollbar=0&toolbar=0&navpanes=0`}
        loading="lazy"
        style={{ ...mediaStyle, border: 'none', background: 'var(--eb-surface)' }}
      />
    );
  }
  return (
    <div
      style={{
        ...mediaStyle,
        background: 'var(--eb-surface-2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 13,
        color: 'var(--eb-muted)',
        fontWeight: '500',
      }}
    >
      Fichier
    </div>
  );
});

/* ============================
   Composant (fonctionnement inchangé)
============================ */
const CashierPromo = () => {
  const [companyUid, setCompanyUid] = useState(null);
  const [merchantPromos, setMerchantPromos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // eslint-disable-next-line
  const [user, setUser] = useState(null);
  // eslint-disable-next-line
  const [showModal, setShowModal] = useState(false);

  const navigate = useNavigate();
  const auth = getAuth();

  // Écoute de l'utilisateur connecté (inchangé côté fonctionnalité)
  useEffect(() => {
    let unsubscribeUser = null;
    const unsubscribeAuth = onAuthStateChanged(auth, (firebaseUser) => {
      if (unsubscribeUser) { unsubscribeUser(); unsubscribeUser = null; }

      if (firebaseUser) {
        const userDocRef = doc(firestore, 'users', firebaseUser.uid);
        unsubscribeUser = onSnapshot(userDocRef, (docSnapshot) => {
          const userData = docSnapshot.data() || null;
          setUser(userData);

          if (!userData?.pseudo) setShowModal(true);

          // ⚠️ évite des re-fetchs inutiles si la valeur ne change pas
          if (userData?.companyuid && userData.companyuid !== companyUid) {
            setCompanyUid(userData.companyuid);
          }
        });
      } else {
        navigate('/logingeneral');
      }
    });

    return () => {
      if (unsubscribeUser) unsubscribeUser();
      unsubscribeAuth();
    };
  }, [auth, navigate, companyUid]);

  // Récupération des promos du commerçant (optimisée coût Storage)
  useEffect(() => {
    const fetchSingleMerchantPromos = async () => {
      setIsLoading(true);
      const cacheKey = companyUid ? `promoCache_${companyUid}` : null;

      try {
        // 1) Essaie cache local (évite lectures Storage si récent)
        if (cacheKey) {
          const cached = readCache(cacheKey);
          if (cached && Array.isArray(cached.merchantPromos)) {
            setMerchantPromos(cached.merchantPromos);
            setIsLoading(false);
            return;
          }
        }

        // 2) Firestore: doc commerçant
        const merchantDocRef = doc(firestore, 'users', companyUid);
        const merchantSnap = await getDoc(merchantDocRef);

        if (!merchantSnap.exists()) {
          console.warn(`Aucun utilisateur commerçant trouvé pour uid = ${companyUid}`);
          setMerchantPromos([]);
          writeCache(cacheKey, { merchantPromos: [] });
          return;
        }

        const merchantData = merchantSnap.data();
        if (merchantData.role !== 'commercant') {
          console.warn(`Le document uid=${companyUid} n'a pas le rôle "commercant".`);
          setMerchantPromos([]);
          writeCache(cacheKey, { merchantPromos: [] });
          return;
        }

        const now = new Date();
        const uid = merchantSnap.id;

        // 3) PUBS — on lit d’abord les métadonnées, on filtre, puis on ne demande l’URL que pour les actives
        let promos = [];
        try {
          const pubRef = ref(storage, `${uid}/pub`);
          const res = await listAll(pubRef);

          // métadonnées (une par item)
          const metaList = await Promise.all(res.items.map(async (item) => {
            const metadata = await getMetadata(item);
            return {
              item,
              name: item.name,
              timeCreated: new Date(metadata.timeCreated),
              start: metadata.customMetadata?.start ? new Date(metadata.customMetadata.start) : null,
              end: (metadata.customMetadata?.end && metadata.customMetadata.end !== 'none') ? new Date(metadata.customMetadata.end) : null,
              contentType: metadata.contentType || '',
            };
          }));

          // filtre actif (période)
          const active = metaList.filter((m) => m.start && now >= m.start && (!m.end || now <= m.end));

          // trie par timeCreated desc
          active.sort((a, b) => b.timeCreated - a.timeCreated);

          // récupère l’URL **uniquement** pour les actives
          const withUrls = await Promise.all(active.map(async (m) => {
            const url = await getDownloadURL(m.item);
            return {
              url,
              name: m.name,
              timeCreated: m.timeCreated,
              start: m.start,
              end: m.end,
              contentType: m.contentType,
            };
          }));

          promos = withUrls;
        } catch (error) {
          console.error(`Erreur lors de la récupération des promos pour uid=${uid}:`, error);
        }

        // 4) LOGO — on lit les métadonnées pour trouver le plus récent, puis on ne charge **qu’une** URL
        let profilePhotoUrl = '';
        try {
          const profileRef = ref(storage, `${uid}/photo-profil`);
          const profileRes = await listAll(profileRef);

          if (profileRes.items.length > 0) {
            const meta = await Promise.all(profileRes.items.map(async (item) => {
              const metadata = await getMetadata(item);
              return { item, timeCreated: new Date(metadata.timeCreated) };
            }));
            meta.sort((a, b) => b.timeCreated - a.timeCreated);
            profilePhotoUrl = await getDownloadURL(meta[0].item); // une seule URL
          }
        } catch (error) {
          console.error(`Erreur lors de la récupération du logo pour uid=${uid}:`, error);
        }

        // 5) Résultat final identique à avant (affichage complet)
        if (promos.length > 0) {
          const merchantObj = {
            uid,
            pseudo: merchantData.pseudo || '',
            profilePhotoUrl,
            promos,
          };
          const payload = [merchantObj];
          setMerchantPromos(payload);
          writeCache(cacheKey, { merchantPromos: payload });
        } else {
          setMerchantPromos([]);
          writeCache(cacheKey, { merchantPromos: [] });
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des promotions du commerçant :", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (companyUid) {
      fetchSingleMerchantPromos();
    } else {
      setIsLoading(true);
    }
  }, [companyUid]);

  // Navigation vers la vue détaillée d'une pub (identique)
  const handleViewPub = (pubUrl, logoUrl, pseudo) => {
    navigate(
      `/cashierpubviewer?url=${encodeURIComponent(pubUrl)}&logo=${encodeURIComponent(logoUrl || '')}&pseudo=${encodeURIComponent(pseudo || '')}`
    );
  };

  return (
    <div>
      <CashierNavbar />
      <div className="dashboard-container">
        <style>{`
          .cp-page { width: 100%; }
          .cp-page .eb-page-title { margin: 0; text-align: left; }
          .cp-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 320px), 1fr)); gap: 24px; }
          .cp-state { grid-column: 1 / -1; padding: 48px 24px; display: flex; flex-direction: column; align-items: center; gap: 16px; text-align: center; color: var(--eb-muted); font-size: 14.5px; }
          .cp-merchant { padding: 24px; }
          .cp-merchant__head { display: flex; align-items: center; gap: 14px; margin-bottom: 20px; padding-bottom: 16px; border-bottom: 1px solid var(--eb-border); min-width: 0; }
          .cp-merchant__logo { width: 52px; height: 52px; border-radius: 50%; object-fit: cover; border: 1px solid var(--eb-border); flex-shrink: 0; }
          .cp-merchant__name { margin: 0; font-size: 18px; font-weight: 650; color: var(--eb-text); text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .cp-thumbs { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 12px; }
          .cp-thumb { cursor: pointer; aspect-ratio: 1 / 1; overflow: hidden; border-radius: var(--eb-radius); border: 1px solid var(--eb-border); background: var(--eb-surface-2); box-shadow: var(--eb-shadow-xs); transition: box-shadow .15s ease, transform .15s ease, border-color .15s ease; -webkit-tap-highlight-color: transparent; }
          .cp-thumb:hover { box-shadow: var(--eb-shadow-md); border-color: var(--eb-border-strong); transform: translateY(-2px); }
          .cp-thumb:focus-visible { outline: 2px solid var(--eb-brand-500); outline-offset: 2px; }
          @media (max-width: 480px) {
            .cp-merchant { padding: 16px; }
            .cp-thumbs { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
          }
        `}</style>

        <div className="cp-page">
          <header className="eb-page-header">
            <div>
              <h1 className="eb-page-title">Promotions</h1>
              <p className="eb-page-subtitle">Les offres en cours de votre établissement.</p>
            </div>
          </header>

          <div className="cp-grid">
            {/* 1) Loading */}
            {isLoading && (
              <div className="eb-card cp-state">
                <span className="eb-spinner" aria-hidden="true" />
                Chargement des promotions...
              </div>
            )}

            {/* 2) Empty */}
            {!isLoading && merchantPromos.length === 0 && (
              <div className="eb-card cp-state">
                Aucune promotion active pour le moment.
              </div>
            )}

            {/* 3) Merchant + toutes les promos */}
            {!isLoading && merchantPromos.length > 0 && merchantPromos.map((merchant) => (
              <div key={merchant.uid} className="eb-card cp-merchant">
                <div className="cp-merchant__head">
                  {merchant.profilePhotoUrl && (
                    <img loading="lazy" src={merchant.profilePhotoUrl} alt="Logo" className="cp-merchant__logo" />
                  )}
                  <h2 className="cp-merchant__name">{merchant.pseudo}</h2>
                </div>

                <div className="cp-thumbs">
                  {merchant.promos.map((pub, idx) => (
                    <div
                      key={idx}
                      className="cp-thumb"
                      onClick={() => handleViewPub(pub.url, merchant.profilePhotoUrl, merchant.pseudo)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleViewPub(pub.url, merchant.profilePhotoUrl, merchant.pseudo);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      title={pub.name || 'Promotion'}
                    >
                      <Thumb pub={pub} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashierPromo;
